import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { AUTH_ENABLED, getToken } from '../auth'
// previewUrl removed — signed URLs are now resolved at render time in components

// BFF origin — empty string (relative URLs) for local dev, full URL for CDN-hosted UI
const BFF_URL = import.meta.env.VITE_BFF_URL || ''

// Types for API responses
export interface ImageDocument {
  master_id: string
  tenant_id: string
  file_path: string
  file_name: string
  file_size_bytes: number
  mime_type: string
  width: number
  height: number
  created_at: string
  ingested_at: string
  caption?: string
  tags: string[]
  classification: {
    is_photo: boolean
    is_screenshot: boolean
    has_faces: boolean
  }
  collection_ids: string[]
  face_ids: string[]
  embedding?: number[]
}

export interface SearchHit {
  score: number
  document: ImageDocument
}

export interface SearchResponse {
  hits: SearchHit[]
  total: number
}

export interface CropBox {
  x: number      // 0-1 range (percentage)
  y: number      // 0-1 range (percentage)
  width: number  // 0-1 range (percentage)
  height: number // 0-1 range (percentage)
}

export interface SearchResult {
  master_id: string
  score: number
  similarity_percent: number
  thumbnail_url: string
  caption?: string
  file_name: string
  width: number
  height: number
  classification?: {
    is_photo: boolean
    is_screenshot: boolean
    has_faces: boolean
  }
}

export interface ImageListResponse {
  items: ImageDocument[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

export interface ServiceStatus {
  name: string
  status: 'running' | 'stopped' | 'starting' | 'error'
  port: number
  gpu?: number
  health?: boolean
}

export interface GpuStatus {
  index: number
  name: string
  memory_used_mb: number
  memory_total_mb: number
  utilization_percent: number
}

export interface AdminStatus {
  services: ServiceStatus[]
  gpus: GpuStatus[]
  queue: {
    pending: number
    in_progress: number
    failed: number
  }
}

export interface PipelineStages {
  analyze: boolean
  normalize: boolean
  classify: boolean
  embed_jina: boolean
  embed_colpali: boolean | null
  embed_faces: boolean | null
  caption: boolean
  tags: boolean
  indexed: boolean
}

export interface FileEntry {
  master_id: string
  filename: string
  source_path: string
  mime_type: string
  file_size_bytes: number
  created_at: string | null
  pipeline_stages: PipelineStages
  classification: string | null
  has_caption: boolean
  has_tags: boolean
  tag_count: number
  indexed_in_es: boolean
}

export interface FileListResponse {
  items: FileEntry[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

export interface FilesSummary {
  total: number
  fully_processed: number
  partially_processed: number
  needs_caption: number
  needs_tags: number
  not_started: number
  failed: number
}

export interface Collection {
  collection_id: string
  tenant_id: string
  name: string
  description?: string
  type: 'album' | 'auto_group' | 'project'
  image_count: number
  cover_image_id?: string
  parent_collection_id?: string
  created_at: string
  updated_at: string
}

// Face overlay types
export interface FaceBBox {
  x: number
  y: number
  width: number
  height: number
}

export interface FaceSummary {
  face_id: string
  master_id: string
  bbox: FaceBBox
  detection_confidence: number
  crop_url: string
  age?: number
  gender?: string
}

export interface FacesForImageResponse {
  master_id: string
  faces: FaceSummary[]
  total: number
}

// Face list types (for FacesPage)
export interface FaceListItem {
  face_id: string
  master_id: string
  crop_url: string
  detection_confidence: number
  is_primary: boolean
  created_at: string | null
  person_id: string | null
  person_name: string | null
}

export interface FaceListResponse {
  faces: FaceListItem[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

// Face detail (for profile view)
export interface FaceDetail {
  face_id: string
  master_id: string
  crop_url: string
  detection_confidence: number
  is_primary: boolean
  created_at: string | null
  person_id: string | null
  person_name: string | null
  bbox: FaceBBox | null
  age: number | null
  gender: string | null
  quality_score: number | null
  source_file_name: string | null
  source_thumbnail_url: string | null
}

// Person types
export interface PersonSummary {
  person_id: string
  name: string | null
  is_known: boolean
  face_count: number
  image_count: number
  prototype_face_id: string | null
  prototype_crop_url: string | null
  created_at: string | null
  updated_at: string | null
}

export interface PersonDetail extends PersonSummary {
  face_ids: string[]
}

export interface PersonListResponse {
  persons: PersonSummary[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

// Deleted face types
export interface DeletedFaceItem {
  face_id: string
  master_id: string
  crop_url: string
  detection_confidence: number
  deleted_at: string | null
  person_id: string | null
  person_name: string | null
}

export interface DeletedFacesResponse {
  faces: DeletedFaceItem[]
  total: number
}

// Cluster suggestion types
export interface ClusterSuggestion {
  suggestion_id: string
  face_ids: string[]
  representative_face_id: string
  representative_crop_url: string
  crop_urls: string[]
  avg_similarity: number
  face_count: number
}

export interface ClusterSuggestionsResponse {
  suggestions: ClusterSuggestion[]
  total_unassigned: number
  total_suggestions: number
}

// Create person from faces response
export interface CreatePersonFromFacesResponse {
  person_id: string
  name: string | null
  face_count: number
  image_count: number
  prototype_face_id: string
}

const transformSearchHits = (response: { hits: Array<{ image: ImageDocument; score: number }> }) =>
  response.hits.map((hit) => ({
    master_id: hit.image.master_id,
    score: hit.score,
    similarity_percent: Math.round(hit.score * 100),
    thumbnail_url: '',  // Resolved at render time via previewUrl() to avoid stale signed URLs
    caption: hit.image.caption,
    file_name: hit.image.file_name,
    width: hit.image.width,
    height: hit.image.height,
    classification: hit.image.classification,
  }))

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BFF_URL}/api`,
    prepareHeaders: async (headers) => {
      if (AUTH_ENABLED) {
        const token = await getToken()
        if (token) {
          headers.set('Authorization', `Bearer ${token}`)
        }
      } else {
        headers.set('X-Tenant-ID', 'local_default')
      }
      return headers
    },
  }),
  tagTypes: ['Images', 'Collections', 'Faces', 'Tags', 'Services', 'Files', 'Persons'],
  endpoints: (builder) => ({
    // Images
    getImages: builder.query<
      ImageListResponse,
      {
        page?: number
        pageSize?: number
        tags?: string[]
        isPhoto?: boolean
        isScreenshot?: boolean
        hasFaces?: boolean
      }
    >({
      query: ({ page = 1, pageSize = 50, tags, isPhoto, isScreenshot, hasFaces }) => {
        const params = new URLSearchParams()
        params.set('page', String(page))
        params.set('page_size', String(pageSize))
        if (tags && tags.length > 0) {
          tags.forEach((tag) => params.append('tags', tag))
        }
        if (isPhoto !== undefined) params.set('is_photo', String(isPhoto))
        if (isScreenshot !== undefined) params.set('is_screenshot', String(isScreenshot))
        if (hasFaces !== undefined) params.set('has_faces', String(hasFaces))
        return `/images?${params.toString()}`
      },
      providesTags: ['Images'],
    }),

    getImage: builder.query<ImageDocument, string>({
      query: (masterId) => `/images/${masterId}`,
      providesTags: (_result, _error, id) => [{ type: 'Images', id }],
    }),

    updateImage: builder.mutation<ImageDocument, { masterId: string; updates: Partial<ImageDocument> }>({
      query: ({ masterId, updates }) => ({
        url: `/images/${masterId}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { masterId }) => [{ type: 'Images', id: masterId }],
    }),

    // Search
    searchSimilar: builder.query<SearchResponse, { embedding: number[]; k?: number; tags?: string[] }>({
      query: ({ embedding, k = 20, tags }) => ({
        url: '/search/similar',
        method: 'POST',
        body: { embedding, k, num_candidates: k * 5, filter_tags: tags },
      }),
    }),

    searchByTag: builder.query<SearchResponse, { tags: string[]; matchAll?: boolean; limit?: number }>({
      query: ({ tags, matchAll = false, limit = 50 }) =>
        `/search/by-tag?tags=${tags.join(',')}&match_all=${matchAll}&limit=${limit}`,
    }),

    searchByCaption: builder.query<SearchResponse, { query: string; limit?: number }>({
      query: ({ query, limit = 50 }) =>
        `/search/by-caption?q=${encodeURIComponent(query)}&limit=${limit}`,
    }),

    // Vector search endpoints
    searchByText: builder.query<SearchResult[], { query: string; limit?: number }>({
      query: ({ query, limit = 20 }) => ({
        url: '/search/text',
        method: 'POST',
        body: { query, limit },
      }),
      transformResponse: transformSearchHits,
    }),

    searchByImage: builder.query<SearchResult[], { masterId: string; limit?: number }>({
      query: ({ masterId, limit = 20 }) => ({
        url: '/search/image',
        method: 'POST',
        body: { master_id: masterId, limit },
      }),
      transformResponse: transformSearchHits,
    }),

    searchByRegion: builder.mutation<SearchResult[], { masterId: string; cropBox: CropBox; limit?: number }>({
      query: ({ masterId, cropBox, limit = 20 }) => ({
        url: '/search/region',
        method: 'POST',
        body: { master_id: masterId, crop_box: cropBox, limit },
      }),
      transformResponse: transformSearchHits,
    }),

    // Collections
    getCollections: builder.query<Collection[], void>({
      query: () => '/collections',
      providesTags: ['Collections'],
    }),

    getCollection: builder.query<Collection, string>({
      query: (id) => `/collections/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Collections', id }],
    }),

    createCollection: builder.mutation<Collection, Partial<Collection>>({
      query: (collection) => ({
        url: '/collections',
        method: 'POST',
        body: collection,
      }),
      invalidatesTags: ['Collections'],
    }),

    updateCollection: builder.mutation<Collection, { id: string; updates: Partial<Collection> }>({
      query: ({ id, updates }) => ({
        url: `/collections/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Collections', id }],
    }),

    deleteCollection: builder.mutation<void, string>({
      query: (id) => ({
        url: `/collections/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Collections'],
    }),

    addToCollection: builder.mutation<void, { collectionId: string; imageIds: string[] }>({
      query: ({ collectionId, imageIds }) => ({
        url: `/collections/${collectionId}/images`,
        method: 'POST',
        body: { image_ids: imageIds },
      }),
      invalidatesTags: ['Collections', 'Images'],
    }),

    // Admin
    getAdminStatus: builder.query<AdminStatus, void>({
      query: () => '/admin/status',
      providesTags: ['Services'],
    }),

    startService: builder.mutation<void, string>({
      query: (serviceName) => ({
        url: `/admin/services/${serviceName}/start`,
        method: 'POST',
      }),
      invalidatesTags: ['Services'],
    }),

    stopService: builder.mutation<void, string>({
      query: (serviceName) => ({
        url: `/admin/services/${serviceName}/stop`,
        method: 'POST',
      }),
      invalidatesTags: ['Services'],
    }),

    restartService: builder.mutation<void, string>({
      query: (serviceName) => ({
        url: `/admin/services/${serviceName}/restart`,
        method: 'POST',
      }),
      invalidatesTags: ['Services'],
    }),

    getServiceLogs: builder.query<{ logs: string[] }, { serviceName: string; lines?: number }>({
      query: ({ serviceName, lines = 100 }) =>
        `/admin/services/${serviceName}/logs?lines=${lines}`,
    }),

    // Tags
    getTags: builder.query<{ name: string; count: number }[], void>({
      query: () => '/tags',
      transformResponse: (response: { tags: { name: string; count: number }[]; total: number }) =>
        response.tags,
      providesTags: ['Tags'],
    }),

    // Files
    getFiles: builder.query<
      FileListResponse,
      { page?: number; pageSize?: number; status?: string }
    >({
      query: ({ page = 1, pageSize = 50, status }) => {
        const params = new URLSearchParams()
        params.set('page', String(page))
        params.set('page_size', String(pageSize))
        if (status) params.set('status', status)
        return `/files?${params.toString()}`
      },
      providesTags: ['Files'],
    }),

    getFilesSummary: builder.query<FilesSummary, void>({
      query: () => '/files/summary',
      providesTags: ['Files'],
    }),

    scanDirectory: builder.query<
      { new: Array<{ path: string; filename: string; size_bytes: number; master_id: string; status: string }>; existing: Array<{ path: string; filename: string; size_bytes: number; master_id: string; status: string }>; total_scanned: number; error?: string },
      { directory: string; recursive?: boolean }
    >({
      query: ({ directory, recursive = true }) =>
        `/files/scan?directory=${encodeURIComponent(directory)}&recursive=${recursive}`,
    }),

    // Faces
    getFacesForImage: builder.query<FacesForImageResponse, string>({
      query: (masterId) => `/faces/by-image/${masterId}`,
      providesTags: (_result, _error, id) => [{ type: 'Faces', id }],
    }),

    getFace: builder.query<FaceDetail, string>({
      query: (faceId) => `/faces/${faceId}`,
      providesTags: (_result, _error, id) => [{ type: 'Faces', id }],
    }),

    listFaces: builder.query<
      FaceListResponse,
      { page?: number; pageSize?: number; primaryOnly?: boolean }
    >({
      query: ({ page = 1, pageSize = 50, primaryOnly = false }) => {
        const params = new URLSearchParams()
        params.set('page', String(page))
        params.set('page_size', String(pageSize))
        if (primaryOnly) params.set('primary_only', 'true')
        return `/faces/?${params.toString()}`
      },
      providesTags: ['Faces'],
    }),

    searchByFace: builder.query<SearchResult[], { faceId: string; limit?: number }>({
      query: ({ faceId, limit = 20 }) => ({
        url: '/search/face',
        method: 'POST',
        body: { face_id: faceId, limit },
      }),
      transformResponse: (response: {
        hits: Array<{ image: ImageDocument; score: number; matched_face_id: string; matched_face_crop_url: string }>
      }) =>
        response.hits.map((hit) => ({
          master_id: hit.image.master_id,
          score: hit.score,
          similarity_percent: Math.round(hit.score * 100),
          thumbnail_url: '',  // Resolved at render time via previewUrl() to avoid stale signed URLs
          caption: hit.image.caption,
          file_name: hit.image.file_name,
          width: hit.image.width,
          height: hit.image.height,
          classification: hit.image.classification,
        })),
    }),

    // Face management mutations
    createPersonFromFaces: builder.mutation<
      CreatePersonFromFacesResponse,
      { faceIds: string[]; name?: string }
    >({
      query: ({ faceIds, name }) => ({
        url: '/faces/create-person-from-faces',
        method: 'POST',
        body: { face_ids: faceIds, name },
      }),
      invalidatesTags: ['Faces', 'Persons'],
    }),

    assignPerson: builder.mutation<
      { linked: number; person_id: string },
      { faceIds: string[]; personId: string }
    >({
      query: ({ faceIds, personId }) => ({
        url: '/faces/assign-person',
        method: 'POST',
        body: { face_ids: faceIds, person_id: personId },
      }),
      invalidatesTags: ['Faces', 'Persons'],
    }),

    unlinkPerson: builder.mutation<{ unlinked: number }, { faceIds: string[] }>({
      query: ({ faceIds }) => ({
        url: '/faces/unlink-person',
        method: 'POST',
        body: { face_ids: faceIds },
      }),
      invalidatesTags: ['Faces', 'Persons'],
    }),

    softDeleteFaces: builder.mutation<{ deleted: number }, { faceIds: string[] }>({
      query: ({ faceIds }) => ({
        url: '/faces/soft-delete',
        method: 'POST',
        body: { face_ids: faceIds },
      }),
      invalidatesTags: ['Faces', 'Persons'],
    }),

    restoreFaces: builder.mutation<{ restored: number }, { faceIds: string[] }>({
      query: ({ faceIds }) => ({
        url: '/faces/restore',
        method: 'POST',
        body: { face_ids: faceIds },
      }),
      invalidatesTags: ['Faces'],
    }),

    permanentDeleteFaces: builder.mutation<{ deleted: number }, { faceIds: string[] }>({
      query: ({ faceIds }) => ({
        url: '/faces/permanent-delete',
        method: 'POST',
        body: { face_ids: faceIds },
      }),
      invalidatesTags: ['Faces', 'Images'],
    }),

    getDeletedFaces: builder.query<DeletedFacesResponse, void>({
      query: () => '/faces/deleted',
      providesTags: ['Faces'],
    }),

    clusterFaces: builder.mutation<ClusterSuggestionsResponse, { threshold?: number }>({
      query: ({ threshold = 0.55 }) => ({
        url: `/faces/cluster?threshold=${threshold}`,
        method: 'POST',
      }),
    }),

    acceptSuggestion: builder.mutation<
      CreatePersonFromFacesResponse,
      { suggestionId: string; name?: string; faceIds?: string[] }
    >({
      query: ({ suggestionId, name, faceIds }) => ({
        url: `/faces/suggestions/${suggestionId}/accept`,
        method: 'POST',
        body: { name, face_ids: faceIds },
      }),
      invalidatesTags: ['Faces', 'Persons'],
    }),

    rejectSuggestion: builder.mutation<{ rejected: boolean }, string>({
      query: (suggestionId) => ({
        url: `/faces/suggestions/${suggestionId}/reject`,
        method: 'POST',
      }),
    }),

    faceFeedback: builder.mutation<
      { action: string; face_id: string; person_id: string },
      { faceId: string; personId: string; isMatch: boolean }
    >({
      query: ({ faceId, personId, isMatch }) => ({
        url: '/faces/feedback',
        method: 'POST',
        body: { face_id: faceId, person_id: personId, is_match: isMatch },
      }),
      invalidatesTags: ['Faces', 'Persons'],
    }),

    searchFeedback: builder.mutation<
      { feedback_id: string; stored: boolean },
      { queryContext: string; masterId: string; relevant: boolean }
    >({
      query: ({ queryContext, masterId, relevant }) => ({
        url: '/search/feedback',
        method: 'POST',
        body: { query_context: queryContext, master_id: masterId, relevant },
      }),
    }),

    // Persons
    listPersons: builder.query<
      PersonListResponse,
      { page?: number; pageSize?: number; search?: string }
    >({
      query: ({ page = 1, pageSize = 50, search }) => {
        const params = new URLSearchParams()
        params.set('page', String(page))
        params.set('page_size', String(pageSize))
        if (search) params.set('search', search)
        return `/persons/?${params.toString()}`
      },
      providesTags: ['Persons'],
    }),

    getPerson: builder.query<PersonDetail, string>({
      query: (personId) => `/persons/${personId}`,
      providesTags: (_result, _error, id) => [{ type: 'Persons', id }],
    }),

    createPerson: builder.mutation<PersonSummary, { name?: string }>({
      query: ({ name }) => ({
        url: '/persons/',
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: ['Persons'],
    }),

    updatePerson: builder.mutation<PersonSummary, { personId: string; name?: string; prototype_face_id?: string }>({
      query: ({ personId, ...body }) => ({
        url: `/persons/${personId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Persons', 'Faces'],
    }),

    deletePerson: builder.mutation<void, string>({
      query: (personId) => ({
        url: `/persons/${personId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Persons', 'Faces'],
    }),

    mergePersons: builder.mutation<PersonSummary, { targetPersonId: string; sourcePersonId: string }>({
      query: ({ targetPersonId, sourcePersonId }) => ({
        url: `/persons/${targetPersonId}/merge`,
        method: 'POST',
        body: { source_person_id: sourcePersonId },
      }),
      invalidatesTags: ['Persons', 'Faces'],
    }),

    searchByPerson: builder.query<SearchResult[], { personId: string; limit?: number }>({
      query: ({ personId, limit = 20 }) => ({
        url: '/search/person',
        method: 'POST',
        body: { person_id: personId, limit },
      }),
      transformResponse: (response: {
        hits: Array<{ image: ImageDocument; score: number; matched_face_id: string; matched_face_crop_url: string }>
      }) =>
        response.hits.map((hit) => ({
          master_id: hit.image.master_id,
          score: hit.score,
          similarity_percent: Math.round(hit.score * 100),
          thumbnail_url: '',  // Resolved at render time via previewUrl() to avoid stale signed URLs
          caption: hit.image.caption,
          file_name: hit.image.file_name,
          width: hit.image.width,
          height: hit.image.height,
          classification: hit.image.classification,
        })),
    }),
  }),
})

export const {
  useGetImagesQuery,
  useGetImageQuery,
  useUpdateImageMutation,
  useSearchSimilarQuery,
  useSearchByTagQuery,
  useSearchByCaptionQuery,
  useSearchByTextQuery,
  useLazySearchByTextQuery,
  useSearchByImageQuery,
  useLazySearchByImageQuery,
  useSearchByRegionMutation,
  useGetCollectionsQuery,
  useGetCollectionQuery,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useDeleteCollectionMutation,
  useAddToCollectionMutation,
  useGetAdminStatusQuery,
  useStartServiceMutation,
  useStopServiceMutation,
  useRestartServiceMutation,
  useGetServiceLogsQuery,
  useGetTagsQuery,
  useGetFilesQuery,
  useGetFilesSummaryQuery,
  useLazyScanDirectoryQuery,
  useGetFacesForImageQuery,
  useGetFaceQuery,
  useListFacesQuery,
  useLazySearchByFaceQuery,
  // Person & face management
  useCreatePersonFromFacesMutation,
  useAssignPersonMutation,
  useUnlinkPersonMutation,
  useSoftDeleteFacesMutation,
  useRestoreFacesMutation,
  usePermanentDeleteFacesMutation,
  useGetDeletedFacesQuery,
  useClusterFacesMutation,
  useAcceptSuggestionMutation,
  useRejectSuggestionMutation,
  useFaceFeedbackMutation,
  useSearchFeedbackMutation,
  useListPersonsQuery,
  useGetPersonQuery,
  useCreatePersonMutation,
  useUpdatePersonMutation,
  useDeletePersonMutation,
  useMergePersonsMutation,
  useLazySearchByPersonQuery,
} = api
